/* eslint-disable react-hooks/exhaustive-deps */
import { App } from '@capacitor/app';
import { Clipboard } from '@capacitor/clipboard';
import { PluginListenerHandle } from '@capacitor/core';
import { modalController } from '@ionic/core';
import {
  IonAlert,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonPage,
  IonRow,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar, useIonModal
} from '@ionic/react';
import ScanButton from 'components/ScanButton/ScanButton';
import { AuthContext } from 'contexts/AuthContext';
import { chevronBackOutline, scanOutline } from 'ionicons/icons';
import {
  useCallback, useContext, useEffect,
  useMemo,
  useState
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AccountStore } from 'stores/AccountStore';
import { WithdrawStore } from 'stores/WithdrawStore';
import client from 'utils/Api';
import currencyFormat, { toFiatValue } from 'utils/Currency';
import WithdrawPreviewModal from './WithdrawPreviewModal';
import './WithdrawScreen.css';
import WithdrawSuccessModal from './WithdrawSuccessModal/WithdrawSuccessModal';


const styles = {
  textAlignCenter: {
    textAlign: 'center'
  },
  spinnerSmall: { height: 10, width: 10, marginLeft: 5 },
  margin0: {
    margin: 0
  },
  dollarSign: {
    position: 'absolute',
    bottom: 10,
    fontSize: 32,
    fontWeight: 700
  },
  marginLeft12: {
    marginLeft: 12
  }
};

export type WithdrawType = 'chain' | 'ln';

const WithdrawScreen: React.FC 
 = () => {
  const history = useHistory();
  const { authState } = useContext(AuthContext);
  const { type: withdrawType, id: provider } = useParams<any>();
  const axiosClient = client(authState.token);
  const { balances } = AccountStore.useState<any>((s:any) => s);
  const [rates, setRates] = useState<any>();
  const loading = WithdrawStore.useState((s:any) => s.loading);
  const [charge, setClipboardCharge] = useState<any>();
  const {openPreviewModal, openSuccessModal, withdrawState} = WithdrawStore.useState<any>((s:any) => s);

  const balance = useMemo(
    () =>
      balances.reduce((totalValue:number, currentValue:any) => {
        if(currentValue.provider === provider){
          return totalValue + currentValue.balance
        }
        return totalValue;
      }, 0),
    [balances]
  );

  const decodeInvoice = async (invoice: string) =>
    await axiosClient.post('/withdrawals/decode', {
      ln_invoice: invoice
    });

  const onScanInvoice = useCallback(
    async (pay_req?: string) => {
      if (typeof pay_req !== 'string') return alert('Invalid QR code!');
      pay_req = pay_req.replace('lightning:', '');
      try {
        const res = await decodeInvoice(pay_req);

        if (res.data.charge.pay_req.amount > balance)
          return alert(`Can't withdraw more than balance amount!`);

        WithdrawStore.update((s:any) => {
          s.withdrawState = {
            ln_invoice: pay_req,
            wallet_address: pay_req,
            amount: res.data.charge.pay_req.amount,
            fiat_value: toFiatValue(res.data.charge.pay_req.amount, rates?.USD)
          };
          s.openPreviewModal = true;
        });
      } catch (e) {
        alert('Something went wrong!');
      } finally {
        WithdrawStore.update((s:any) => {
          s.loading = false;
        })
      }
    },
    [rates]
  );

  const getChargeFromClipboard = useCallback(async () => {
    try {
      let result = await Clipboard.read();
      if (result.type === 'text/plain' && result.value.startsWith('ln')) {
        const res = await decodeInvoice(result.value);

        if (res?.data?.charge) {
          setClipboardCharge({ ...res.data.charge, ln_invoice: result.value });
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, [setClipboardCharge]);

  const openPreview = useCallback(
    async (useClipboard = false) => {
      if (useClipboard) {
        WithdrawStore.update((s:any) => {
          s.withdrawState = {
            ...s.withdrawState,
            ln_invoice: charge?.ln_invoice,
            amount: charge?.pay_req.amount,
            fiat_value: toFiatValue(charge?.pay_req.amount, rates?.USD)
          };
          s.openPreviewModal = true;
        });
      } else if (withdrawType === 'ln' && withdrawState) {
        const res = await decodeInvoice(withdrawState.ln_invoice);
        WithdrawStore.update((s:any) => {
          s.withdrawState = {
            ...s.withdrawState,
            amount: res.data.charge.pay_req.amount,
            fiat_value: toFiatValue(res.data.charge.pay_req.amount, rates?.USD)
          };
          s.openPreviewModal = true;
        });
      }
    },
    [charge, withdrawType, withdrawState]
  );

  useEffect(() => {
    var listener:PluginListenerHandle;

    (async () => {
        await getChargeFromClipboard();
        listener = await App.addListener(
          'appStateChange',
          async (state: { isActive: boolean }) => {
            if (state.isActive) {
              getChargeFromClipboard();
            }
          }
        );
    })();

    return () => {
        listener?.remove();
    };
  }, []);

  async function getCurrencyRate() {
    const res = await axiosClient.get('/rates');
    setRates(res.data.rate);
  }

  const postData = async (sats_amount?: number, fiat_amount?: number) => {
    try {
      const payload = {
        ...withdrawState,
        amount: sats_amount && Math.floor(sats_amount),
        fiat_amount,
        type: withdrawType,
        provider: provider
      };

      await axiosClient.post('/account/managed/withdrawals', payload);
      WithdrawStore.update((s:any) => {
          s.loading = false;
      });
      await handleDismissPreview();
      await WithdrawStore.update((s:any) => {
          s.openSuccessModal = true;
      });
    } catch (e:any) {
      WithdrawStore.update((s:any) => {
          s.loading = false;
      });
      if (e?.response?.status === 400)
        alert('Make sure you have the available funds.');
      else
        alert('Something went wrong!');
    } 
  };

  useEffect(() => {
    !rates && getCurrencyRate().then();
  }, [setRates, axiosClient]);

  const onInputChange = (e: CustomEvent) =>
    // @ts-ignore
    setState((prevState) => ({
      ...prevState,
      // @ts-ignore
      [e.target?.name]: e.target?.value
    }));

  const onSubmit = async () => {
    if (
      withdrawState.amount &&
      balance &&
      (withdrawState.amount > balance || withdrawState < 0)
    ){
      WithdrawStore.update((s:any) => {
        s.loading = false;
      })
      return alert('Cannot withdraw more than available amount!');
    }
    
    if (withdrawState?.ln_invoice){
      await postData();
    }else{
      alert('Invalid lightning invoice!');
      WithdrawStore.update((s:any) => {
        s.loading = false;
      })
    }
  };

  const isSubmittable = useMemo(
    () =>
      Boolean(
        (withdrawState?.ln_invoice) &&
          (withdrawState?.amount || withdrawState?.fiat_value)
      ),
    [withdrawState]
  );

  const getFromClipboard = async () => {
    let result = await Clipboard.read();
    if (result.type === 'text/plain' && result.value.startsWith('ln')) {
      const res = await decodeInvoice(result.value);

      if (res?.data?.charge) {
        setClipboardCharge({ ...res.data.charge, ln_invoice: result.value });
      }
    }
  };

    //success modal logic
  const handleDismissSuccess = async () => {
    WithdrawStore.update((s:any) => {
      s.withdrawState = {};
      s.openSuccessModal = false;
    });
    await Clipboard.write({
      string: ""
    });
    dismissSuccess();
    history.goBack();
  };

  const [presentSuccess, dismissSuccess] = useIonModal(WithdrawSuccessModal, {
    amount: withdrawState?.amount!,
    onDismissSuccess: handleDismissSuccess,
  });
  //end success modal logic


  //preview modal logic
  const handleDismissPreview = () => {
    WithdrawStore.update((s:any) => {
      s.openPreviewModal = false;
    });
    return dismissPreview();
  };

  const [presentPreview, dismissPreview] = useIonModal(WithdrawPreviewModal, {
    fiat_value: withdrawState?.fiat_value,
    isBTC: true,
    onDismiss: handleDismissPreview,
    onSubmit: onSubmit,
    sats_amount: withdrawState?.amount,
    state: withdrawState,
    withdrawType: withdrawType
  });
  //end preview modal logic

  useEffect(() => {
   async function openPreviewModalFn() {
      presentPreview({
        swipeToClose: false,
        cssClass: "rounded",
        showBackdrop: false,
        backdropDismiss: false,
        presentingElement: await modalController.getTop(),
      });
    }
    async function openSuccessModalFn() {
      presentSuccess({
        swipeToClose: false,
        cssClass: "rounded",
        showBackdrop: false,
        backdropDismiss: false,
        presentingElement: await modalController.getTop(),
      });
    }
    if(openPreviewModal) openPreviewModalFn();
    if(openSuccessModal) openSuccessModalFn();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[openPreviewModal, openSuccessModal])

  return (
    <IonPage className={'withdraw-screen-root'}>
      <IonHeader className={"ion-no-border"}>
        <IonToolbar className={"background-color"}>
          <IonButtons slot="start">
            <IonButton onClick={() => history.goBack()}>
              <IonIcon icon={chevronBackOutline} color="dark" size="large" />
            </IonButton>
          </IonButtons>
          <IonTitle className={'ion-text-capitalize'}>Withdraw from {provider}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonGrid>
          <IonRow>
            <IonCol>
              <div
                className={'amount-card'}
                style={{
                  backgroundImage: `url: ${require('assets/bg/withdraw-card.svg')}`
                }}>
                <div className={'mask'} />
                <IonItem lines={'none'}>
                  <IonLabel position={'stacked'}>
                    Amount
                  </IonLabel>
                  <IonInput
                    disabled={true}
                    inputmode={'decimal'}
                    name={'amount'}
                    onIonInput={onInputChange}
                    placeholder={'0'}
                    type={'number'}
                    value={withdrawState?.amount}
                  />
                </IonItem>
                <div className={'text-container'}>
                  <IonText>
                    <p className={'withdrawable-text'}>
                      Withdrawable Sats{': '}
                      <span>
                        {rates ? (
                          `${balance} sats`
                        ) : (
                          <IonSpinner
                            name={'circular'}
                            style={styles.spinnerSmall}
                          />
                        )}
                      </span>
                      <br />
                      <small>
                        ~ $
                        {currencyFormat(
                          toFiatValue(balance, rates?.USD).toFixed(2)
                        )}
                      </small>
                    </p>
                  </IonText>
                </div>
              </div>
              <IonItem className={'bitcoin-address'} lines={'none'}>
                <IonLabel position={'stacked'}>
                  Lightning Address
                </IonLabel>
                <IonInput
                  name={'ln_invoice'}
                  clearOnEdit={true}
                  onClick={getFromClipboard}
                  placeholder={
                    'Tap to paste your lightning invoice'
                  }
                  value={withdrawState?.ln_invoice}
                />
                <ScanButton
                  fill={'clear'}
                  onScanFinished={onScanInvoice}
                  authState={authState}>
                  <IonIcon icon={scanOutline}/>
                </ScanButton>
              </IonItem>
            </IonCol>
          </IonRow>
        </IonGrid>
        <IonButton
          className={`submit-button ${isSubmittable ? '' : 'ion-hide'}`}
          disabled={loading || !isSubmittable}
          expand={'block'}
          onClick={() => openPreview()}>
          {loading ? <IonSpinner name={'circular'} /> : 'Withdraw'}
        </IonButton>
      </IonContent>
      <IonAlert
        buttons={[
          { text: "Yes", handler: () => openPreview(true) },
          "No"
        ]}
        isOpen={!!charge}
        message={"You have a lightning invoice in your clipboard. Would you like to use it?"}
        onDidDismiss={() => setClipboardCharge(undefined)}
      />
    </IonPage>
  );
};

export default WithdrawScreen;
